import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listInstances, createInstance, deleteInstance as apiDeleteInstance } from '../api';
import type { Instance } from '../types';

const STORAGE_KEY = 'openclaw_selected_instance_id';
const MAX_INSTANCES = 10;

// Module-level deduplication to prevent duplicate fetches across re-renders
let fetchPromise: Promise<Instance[]> | null = null;
let lastFetchTime = 0;
const FETCH_DEBOUNCE_MS = 500;

interface UseInstancesReturn {
  /** All instances for the user */
  instances: Instance[];
  /** Currently selected instance (null if none) */
  currentInstance: Instance | null;
  /** Whether user has at least one instance */
  hasInstances: boolean;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Maximum number of instances allowed */
  maxInstances: number;
  /** Whether user has reached the instance limit */
  isAtLimit: boolean;

  /** Select a different instance as current */
  selectInstance: (instanceId: string) => void;
  /** Create and start a new instance */
  createAndStart: (name?: string) => Promise<Instance>;
  /** Delete an instance */
  deleteInstance: (instanceId: string) => Promise<void>;
  /** Refresh instance list from server */
  refresh: () => Promise<void>;
  /** Clear error state */
  clearError: () => void;
}

/**
 * Hook for managing user's OpenClaw instances.
 *
 * Supports multiple instances:
 * - Lists all instances for the user
 * - Tracks currently selected instance (persisted to localStorage)
 * - Provides methods to create, select, and delete instances
 */
export function useInstances(): UseInstancesReturn {
  const { session } = useAuth();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [currentInstanceId, setCurrentInstanceId] = useState<string | null>(() => {
    // Initialize from localStorage
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  // Derive currentInstance from instances and currentInstanceId
  const currentInstance = instances.find((inst) => inst.id === currentInstanceId) || instances[0] || null;

  /**
   * Load instances from server.
   * Only shows loading state on initial load.
   * Subsequent refreshes happen silently in background.
   * Uses module-level deduplication to prevent duplicate fetches.
   */
  const loadInstances = useCallback(async (forceRefresh = false) => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    // Debounce: skip if we fetched recently (unless forced)
    const now = Date.now();
    if (!forceRefresh && now - lastFetchTime < FETCH_DEBOUNCE_MS) {
      return;
    }

    // Dedupe: if a fetch is already in progress, reuse it
    if (fetchPromise && !forceRefresh) {
      try {
        const loadedInstances = await fetchPromise;
        setInstances(loadedInstances);
        return;
      } catch {
        // Fall through to retry
      }
    }

    // Only show loading spinner on initial load
    if (!hasLoadedOnce.current) {
      setLoading(true);
    }
    setError(null);

    // Create a new fetch promise
    lastFetchTime = now;
    fetchPromise = listInstances();

    try {
      const loadedInstances = await fetchPromise;
      setInstances(loadedInstances);
      hasLoadedOnce.current = true;

      // If current instance is not in the list, select the first one
      if (loadedInstances.length > 0) {
        const savedId = localStorage.getItem(STORAGE_KEY);
        const savedExists = loadedInstances.some((inst) => inst.id === savedId);
        if (!savedExists) {
          const firstId = loadedInstances[0].id;
          setCurrentInstanceId(firstId);
          localStorage.setItem(STORAGE_KEY, firstId);
        }
      } else {
        setCurrentInstanceId(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load instances';
      console.error('[useInstances] Failed to load instances:', message);
      setError(message);
    } finally {
      setLoading(false);
      fetchPromise = null;
    }
  }, [session?.access_token]);

  /**
   * Select a different instance as current.
   */
  const selectInstance = useCallback((instanceId: string) => {
    const exists = instances.some((inst) => inst.id === instanceId);
    if (exists) {
      setCurrentInstanceId(instanceId);
      try {
        localStorage.setItem(STORAGE_KEY, instanceId);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [instances]);

  /**
   * Create a new instance.
   * Backend automatically triggers start, so we just need to create and poll for updates.
   * Returns immediately with 'starting' status - polling will update to 'running'.
   */
  const createAndStart = useCallback(async (name: string = 'My Agent'): Promise<Instance> => {
    if (!session) {
      throw new Error('Not authenticated');
    }

    if (instances.length >= MAX_INSTANCES) {
      throw new Error(`Maximum of ${MAX_INSTANCES} instances allowed`);
    }

    setError(null);

    try {
      // Create instance - backend auto-triggers start, returns with 'starting' status
      const newInstance = await createInstance(name);

      // Add to instances list with status from backend (should be 'starting')
      setInstances((prev) => [newInstance, ...prev]);

      // Select the new instance
      setCurrentInstanceId(newInstance.id);
      try {
        localStorage.setItem(STORAGE_KEY, newInstance.id);
      } catch {
        // Ignore localStorage errors
      }

      // Polling will automatically refresh and update status to 'running'
      return newInstance;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create instance';
      console.error('[useInstances] Failed to create instance:', message);
      setError(message);
      throw err;
    }
  }, [session, instances.length]);

  /**
   * Delete an instance.
   */
  const deleteInstance = useCallback(async (instanceId: string): Promise<void> => {
    if (!session) {
      throw new Error('Not authenticated');
    }

    setError(null);

    try {
      await apiDeleteInstance(instanceId);

      // Remove from local state
      setInstances((prev) => prev.filter((inst) => inst.id !== instanceId));

      // If we deleted the current instance, select another
      if (currentInstanceId === instanceId) {
        const remaining = instances.filter((inst) => inst.id !== instanceId);
        if (remaining.length > 0) {
          setCurrentInstanceId(remaining[0].id);
          localStorage.setItem(STORAGE_KEY, remaining[0].id);
        } else {
          setCurrentInstanceId(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete instance';
      console.error('[useInstances] Failed to delete instance:', message);
      setError(message);
      throw err;
    }
  }, [session, currentInstanceId, instances]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load instances on mount and when session changes
  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  // Create a refresh function that forces a new fetch
  const refresh = useCallback(() => loadInstances(true), [loadInstances]);

  return {
    instances,
    currentInstance,
    hasInstances: instances.length > 0,
    loading,
    error,
    maxInstances: MAX_INSTANCES,
    isAtLimit: instances.length >= MAX_INSTANCES,
    selectInstance,
    createAndStart,
    deleteInstance,
    refresh,
    clearError,
  };
}

/**
 * Get the shortId from an instance ID (first 8 characters).
 * Used for subdomain routing: claw-{shortId}.openclaw.ski
 */
export function getShortId(instanceId: string): string {
  return instanceId.slice(0, 8);
}
